import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock3, Loader2, Minus, Play, ThumbsDown, ThumbsUp } from "lucide-react";
import { ActiveProposalLifecycleAction, Proposal } from "../hooks/useGovernorProposals";
import { formatEther } from "viem";

interface ProposalCardProps {
  proposal: Proposal;
  onVote: (proposalId: string, support: 0 | 1 | 2) => Promise<void>;
  onQueue: (proposal: Proposal) => Promise<void>;
  onExecute: (proposal: Proposal) => Promise<void>;
  isVoteLoading: boolean;
  isLifecycleLoading: boolean;
  activeLifecycleAction: ActiveProposalLifecycleAction | null;
}

export const ProposalCard = ({
  proposal,
  onVote,
  onQueue,
  onExecute,
  isVoteLoading,
  isLifecycleLoading,
  activeLifecycleAction,
}: ProposalCardProps) => {
  const getStatusVariant = (state: string) => {
    switch (state) {
      case "Active":
        return "default";
      case "Executed":
      case "Succeeded":
      case "Queued":
        return "outline";
      case "Defeated":
      case "Canceled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Parse description to extract title
  const lines = proposal.description.split('\n');
  const title = lines[0]?.replace(/^#\s*/, '') || `Proposal #${proposal.id}`;
  const description = lines.slice(1).join('\n').trim();

  // Calculate vote percentages
  const totalVotes = 
    BigInt(proposal.forVotes) + 
    BigInt(proposal.againstVotes) + 
    BigInt(proposal.abstainVotes);
  
  const forPercent = totalVotes > 0n 
    ? Number((BigInt(proposal.forVotes) * 100n) / totalVotes) 
    : 0;
  const againstPercent = totalVotes > 0n 
    ? Number((BigInt(proposal.againstVotes) * 100n) / totalVotes) 
    : 0;
  const abstainPercent = totalVotes > 0n 
    ? Number((BigInt(proposal.abstainVotes) * 100n) / totalVotes) 
    : 0;

  const canVote = proposal.state === "Active";
  const canQueue = proposal.state === "Succeeded" && proposal.needsQueuing;
  const canExecute = proposal.state === "Queued" || (proposal.state === "Succeeded" && !proposal.needsQueuing);
  const etaMs = proposal.eta ? Number(proposal.eta) * 1_000 : null;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (etaMs === null || now >= etaMs) return;
    const timeout = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(etaMs - now + 250, 2_147_483_647),
    );
    return () => window.clearTimeout(timeout);
  }, [etaMs, now]);

  const executionReady = etaMs === null || now >= etaMs;
  const isThisActionLoading = activeLifecycleAction?.proposalId === proposal.id;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <h4 className="font-medium text-lg">{title}</h4>
              {description && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {description}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={getStatusVariant(proposal.state)}>
                  {proposal.state}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Blocks: {proposal.startBlock} - {proposal.endBlock}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Votes</div>
            <div className="flex gap-6 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>For: {forPercent}% ({formatEther(BigInt(proposal.forVotes))})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>Against: {againstPercent}% ({formatEther(BigInt(proposal.againstVotes))})</span>
              </div>
              {abstainPercent > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                  <span>Abstain: {abstainPercent}% ({formatEther(BigInt(proposal.abstainVotes))})</span>
                </div>
              )}
            </div>

            <div className="w-full h-2 bg-muted rounded-full overflow-hidden flex">
              {forPercent > 0 && (
                <div 
                  className="h-full bg-green-500" 
                  style={{ width: `${forPercent}%` }}
                />
              )}
              {againstPercent > 0 && (
                <div 
                  className="h-full bg-red-500" 
                  style={{ width: `${againstPercent}%` }}
                />
              )}
              {abstainPercent > 0 && (
                <div 
                  className="h-full bg-gray-400" 
                  style={{ width: `${abstainPercent}%` }}
                />
              )}
            </div>
          </div>

          {canVote && (
            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVote(proposal.id, 1)}
                disabled={isVoteLoading}
                className="flex-1"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Vote For
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVote(proposal.id, 0)}
                disabled={isVoteLoading}
                className="flex-1"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                Vote Against
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVote(proposal.id, 2)}
                disabled={isVoteLoading}
              >
                <Minus className="h-4 w-4 mr-2" />
                Abstain
              </Button>
            </div>
          )}

          {(canQueue || canExecute) && (
            <div className="space-y-2 border-t pt-4">
              {canExecute && !executionReady && etaMs !== null && (
                <p className="text-sm text-muted-foreground">
                  Timelock ends {new Date(etaMs).toLocaleString()}. Anyone can execute after that time.
                </p>
              )}
              {canQueue && (
                <Button
                  size="sm"
                  onClick={() => onQueue(proposal)}
                  disabled={isLifecycleLoading}
                >
                  {isThisActionLoading && activeLifecycleAction?.action === "queue" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Clock3 className="mr-2 h-4 w-4" />
                  )}
                  {isThisActionLoading && activeLifecycleAction?.action === "queue" ? "Queueing..." : "Queue proposal"}
                </Button>
              )}
              {canExecute && (
                <Button
                  size="sm"
                  onClick={() => onExecute(proposal)}
                  disabled={isLifecycleLoading || !executionReady}
                >
                  {isThisActionLoading && activeLifecycleAction?.action === "execute" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="mr-2 h-4 w-4" />
                  )}
                  {isThisActionLoading && activeLifecycleAction?.action === "execute" ? "Executing..." : "Execute proposal"}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
